import { ICamera } from '@api/models/camera.model';
import { CircularProgress } from '@library/loaders/components';
import { useRef, useState, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { Link, useSearchParams } from 'react-router-dom';
import CameraCard from '../CameraCard/CameraCard';
import { ChevronDownIcon, InfoCircleIcon } from '@icon/index';
import { Tooltip } from '@library/tooltip';
import useResellerCameraListData from 'modules/reseller-cameras/hooks/useResellerCameraListData';
import { IClient, IProject } from '@api/models';
import SelectDropdown from '@components/SelectDropDown/SelectDropDown';

interface IPaginatedCameres {
    cameras: ICamera[];
    pageCount: number;
}

const ResellerCameraList = () => {
    const filterOptions = [
        { label: `Active`, value: 'active' },
        { label: `Inactive`, value: 'inactive' },
    ];

    const [searchParams, setSearchParams] = useSearchParams();
    const initialFilter = searchParams.get('filter') || 'active';
    const [filterValue, setFilterValue] = useState<string>(initialFilter);

    const { ref } = useInView({ threshold: 0.1 });
    const topBarRef = useRef<HTMLDivElement>(null);

    const { data, isLoading, filteredCamera } = useResellerCameraListData({ filter: filterValue });

    return (
        <PageTemplate ref={topBarRef}>
            {/* Responsive header (matches previous flex approach) */}
            {!isLoading && filteredCamera && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <PageHeading>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <span className="truncate max-w-[85vw] sm:max-w-none">
                                {filterValue === 'active' ? 'Active' : 'In Active'} Cameras - ({data?.count})
                            </span>
                            <Tooltip
                                content="Cameras which have uploaded to the ProgressCenter server in the past 9 Days will be considered active."
                                placement="bottom-end"
                            >
                                <span className="text-grayscale-500 inline-flex">
                                    <InfoCircleIcon className="h-4 w-4" />
                                </span>
                            </Tooltip>
                        </div>
                    </PageHeading>

                    <div className="w-full sm:w-auto">
                        <SelectDropdown
                            className="btn-dropdown btn-secondary btn-secondary-hover w-full sm:w-auto justify-between"
                            value={filterValue}
                            onChange={(value) => {
                                const v = String(value);
                                setFilterValue(v);
                                setSearchParams({ filter: v });
                            }}
                            options={filterOptions}
                            render={(label) => (
                                <span className="flex items-center gap-2 text-grayscale-500 font-medium">
                                    View:{' '}
                                    <span className="text-grayscale-900 font-semibold truncate max-w-[60vw] sm:max-w-none">
                                        {label}
                                    </span>
                                    <ChevronDownIcon className="text-grayscale-400" strokeWidth="2" />
                                </span>
                            )}
                        />
                    </div>
                </div>
            )}

            <div
                style={{
                    height: `${window.innerHeight - (+topBarRef?.current?.offsetHeight || 0)}px`,
                }}
            >
                <div className="mt-4">
                    {isLoading ? (
                        <div className="flex justify-center py-36">
                            <CircularProgress />
                        </div>
                    ) : filteredCamera.length > 0 ? (
                        <div className="grid grid-cols-12 gap-6 mt-6 empty:hidden">
                            {filteredCamera.map((camera, index) => (
                                <div className="col-span-12 sm:col-span-6 lg:col-span-3" key={camera._id}>
                                    <Link
                                        to={`/clients/${(camera.client as IClient)?._id}/projects/${
                                            (camera.project as IProject)?._id
                                        }/cameras/${camera._id}`}
                                    >
                                        <CameraCard
                                            camera={camera}
                                            isActivator={filteredCamera.length - 30 === index}
                                            ref={ref}
                                        />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center pt-36 text-lg h-full w-full text-grayscale-500">
                            No Camera&apos;s Found
                        </div>
                    )}
                </div>
            </div>
        </PageTemplate>
    );
};

export default ResellerCameraList;
